/**
 * ActionRow
 * =========
 *
 * A clickable row used in the informant management summary screen.  Each row
 * represents an available action (e.g. "Switch questionnaires",
 * "Add a second informant", "View details").
 *
 * Renders as a card-like button with an icon, label, optional description,
 * and a chevron indicator.  Matches the existing action row design in the
 * informant management modal (border rounded-md, hover bg-gray-50).
 *
 * When disabled, renders as a <div> instead of a <button> so that any
 * interactive trailing element (e.g. an InfoTooltip) remains clickable —
 * a native disabled button swallows all pointer events from its children.
 */

import React from 'react';
import { ChevronRightIcon } from '@heroicons/react/outline';

/**
 * @param {Object}          props
 * @param {React.ReactNode} props.icon        – Heroicon component or element for the left icon
 * @param {string}          props.label       – Primary text (e.g. "Switch questionnaires")
 * @param {string}          [props.description] – Secondary text
 * @param {Function}        props.onClick     – Click handler
 * @param {boolean}         [props.disabled]  – Whether the action is unavailable
 * @param {string}          [props.variant]   – 'default' | 'primary' | 'warning'
 * @param {React.ReactNode} [props.trailing]  – Element rendered in place of the chevron
 *                                               when disabled (e.g. an InfoTooltip). Must
 *                                               remain interactive, which is why the disabled
 *                                               state uses a <div> wrapper.
 */
export default function ActionRow({
  icon,
  label,
  description,
  onClick,
  disabled = false,
  variant = 'default',
  trailing,
}) {
  const variantClasses = {
    default: 'hover:bg-gray-50',
    primary: 'hover:bg-gray-50',
    warning: 'hover:bg-gray-50',
  };

  const sharedClasses = `
    w-full flex items-center gap-3 p-3 text-left
    border border-gray-200 rounded-md transition-colors
    ${disabled ? 'opacity-50 cursor-not-allowed' : `cursor-pointer ${variantClasses[variant] || variantClasses.default}`}
  `;

  const inner = (
    <>
      <div className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-600" aria-hidden="true">
        {icon}
      </div>

      <div className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-gray-800 truncate">
          {label}
        </span>
        {description && (
          <span className="block text-xs text-gray-500 mt-0.5 line-clamp-2">
            {description}
          </span>
        )}
      </div>

      {disabled
        ? (trailing || null)
        : <ChevronRightIcon className="w-4 h-4 text-gray-400 shrink-0" aria-hidden="true" />
      }
    </>
  );

  if (disabled) {
    return (
      <div role="button" aria-disabled="true" className={sharedClasses}>
        {inner}
      </div>
    );
  }

  return (
    <button type="button" onClick={onClick} className={sharedClasses}>
      {inner}
    </button>
  );
}
